import { useEffect, useRef, useState, useCallback } from "react";
import "leaflet/dist/leaflet.css";
import "leaflet-draw/dist/leaflet.draw.css";
import L from "leaflet";
import "leaflet-draw";
import { api, formatApiErrorDetail } from "@/api";
import { useRealtime } from "@/hooks/useRealtime";
import { confirmDialog } from "@/components/Confirm";
import { useAuth } from "@/context/AuthContext";
import { PRIORITIES } from "@/lib/constants";
import { toast } from "sonner";
import { Trash2, Layers, Building2, Droplet, Flame, Hexagon, PencilRuler } from "lucide-react";
import { MapShell } from "@/components/MapShell";
import { kenmerkMeta } from "@/lib/kenmerkIcons";
import { makeBaseLayer } from "@/lib/mapBase";
import { SharedLayerEditor } from "@/components/SharedLayerEditor";

const spin = (color = "#0d9488") => L.divIcon({ className: "", html: `<div style="width:16px;height:16px;border-radius:50% 50% 50% 0;background:${color};border:2px solid #fff;transform:rotate(-45deg);box-shadow:0 1px 4px rgba(0,0,0,.4)"></div>`, iconAnchor: [8, 14] });
const incIcon = (hex) => L.divIcon({ className: "", html: `<div style="width:20px;height:20px;border-radius:50%;background:${hex};border:3px solid #fff;box-shadow:0 0 0 3px ${hex}55,0 1px 4px rgba(0,0,0,.4)"></div>`, iconAnchor: [10, 10] });
const incLoc = (inc) => {
  const lat = inc.building ? inc.building.lat : inc.location_lat;
  const lng = inc.building ? inc.building.lng : inc.location_lng;
  return lat != null && lng != null ? [lat, lng] : null;
};

const LAYER_META = [
  { key: "buildings", label: "Gebouwen", icon: Building2, color: "#1e40af" },
  { key: "water", label: "Bluswater", icon: Droplet, color: "#0284c7" },
  { key: "incidents", label: "Incidenten", icon: Flame, color: "#dc2626" },
  { key: "shared", label: "Gedeelde laag", icon: PencilRuler, color: "#0d9488" },
  { key: "area", label: "Verzorgingsgebied", icon: Hexagon, color: "#2563eb" },
];

export default function MapView() {
  const { user } = useAuth();
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const drawnRef = useRef(null);
  const areaLayerRef = useRef(null);
  const buildingsGrpRef = useRef(null);
  const waterGrpRef = useRef(null);
  const incidentsGrpRef = useRef(null);
  const sharedRef = useRef(null);
  const baseRef = useRef(null);
  const [buildings, setBuildings] = useState([]);
  const [water, setWater] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [area, setArea] = useState(null);
  const [ready, setReady] = useState(false);
  const [mapCfg, setMapCfg] = useState(null);
  const [sat, setSat] = useState(false);
  const [shared, setShared] = useState(null);
  const [legend, setLegend] = useState(null);
  const [editShared, setEditShared] = useState(false);
  const [layers, setLayers] = useState({ buildings: true, water: true, incidents: true, shared: true, area: true });
  const canEdit = user.role === "beheerder";
  const canDrawShared = ["beheerder", "meldkamer"].includes(user.role);

  const loadIncidents = useCallback(() => {
    api.get("/incidents?status=open").then(({ data }) => setIncidents(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/buildings"), api.get("/settings/service-area"), api.get("/settings/map"),
      api.get("/map-layer").catch(() => ({ data: {} })), api.get("/settings/color-legend").catch(() => ({ data: {} })),
      api.get("/water-points").catch(() => ({ data: [] })), api.get("/incidents?status=open").catch(() => ({ data: [] })),
    ]).then(([b, s, m, ml, cl, wp, inc]) => {
      setBuildings(b.data);
      setArea(s.data.polygon || null);
      setMapCfg(m.data);
      setShared(ml.data && ml.data.enabled !== false ? ml.data.drawings : null);
      setLegend((cl.data && cl.data.legend) || null);
      setWater(wp.data || []);
      setIncidents(inc.data || []);
      setReady(true);
    });
  }, []);

  // Base layer (satellite / static / osm)
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (baseRef.current) map.removeLayer(baseRef.current);
    baseRef.current = makeBaseLayer(mapCfg, sat).addTo(map);
    baseRef.current.bringToBack();
    if (!sat && mapCfg && mapCfg.mode === "static" && mapCfg.bounds) { try { map.fitBounds(mapCfg.bounds); } catch (e) {} }
  }, [sat]); // eslint-disable-line

  const persistArea = useCallback(async (polygon) => {
    try { await api.put("/settings/service-area", { polygon }); toast.success("Verzorgingsgebied opgeslagen"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  }, []);

  // Init map once + build building & water groups + draw controls
  useEffect(() => {
    if (!ready || !mapEl.current || mapRef.current || buildings.length === 0) return;
    const map = L.map(mapEl.current, { zoomControl: true, attributionControl: false }).setView([buildings[0].lat, buildings[0].lng], 14);
    mapRef.current = map;
    baseRef.current = makeBaseLayer(mapCfg, false).addTo(map);
    if (mapCfg && mapCfg.mode === "static" && mapCfg.bounds) { try { map.fitBounds(mapCfg.bounds); } catch (e) {} }

    // Buildings group (click for popup — code chip stays as a small marker)
    const bgrp = new L.FeatureGroup();
    buildings.forEach((b) => {
      const icon = L.divIcon({ className: "", html: `<div style="background:#1e40af;color:#fff;border:2px solid #fff;border-radius:9px;padding:4px 8px;font-size:11px;font-weight:700;white-space:nowrap;box-shadow:0 2px 6px rgba(0,0,0,.3)">${b.code || "•"}</div>`, iconAnchor: [16, 16] });
      const marker = L.marker([b.lat, b.lng], { icon, draggable: false });
      const ken = (b.kenmerken || []).map((k) => `${kenmerkMeta(k).emoji} ${k}`).join("<br/>");
      marker.bindPopup(`<b>${b.name}</b>${b.description ? "<br/>" + b.description : ""}${ken ? "<br/>" + ken : ""}`);
      bgrp.addLayer(marker);
    });
    buildingsGrpRef.current = bgrp;

    // Water group
    const wgrp = new L.FeatureGroup();
    (water || []).forEach((w) => {
      if (w.lat == null || w.lng == null) return;
      const m = L.marker([w.lat, w.lng], { icon: spin("#0284c7") });
      m.bindPopup(`<b>${w.type === "Blusvijver" ? "🌊" : "💧"} ${w.name}</b>${w.capacity ? "<br/>" + w.capacity + " l/min" : ""}${w.note ? "<br/>" + w.note : ""}`);
      wgrp.addLayer(m);
    });
    waterGrpRef.current = wgrp;

    // Service area draw layer
    const drawn = new L.FeatureGroup();
    map.addLayer(drawn);
    drawnRef.current = drawn;

    if (canEdit) {
      const drawControl = new L.Control.Draw({
        position: "topright",
        draw: {
          polygon: { showArea: false, shapeOptions: { color: "#2563eb", weight: 2, fillColor: "#2563eb", fillOpacity: 0.1 } },
          rectangle: { showArea: false, shapeOptions: { color: "#2563eb", weight: 2, fillColor: "#2563eb", fillOpacity: 0.1 } },
          polyline: false, circle: false, marker: false, circlemarker: false,
        },
        edit: { featureGroup: drawn, remove: false },
      });
      map.addControl(drawControl);
      const coordsOf = (layer) => layer.getLatLngs()[0].map((p) => [Number(p.lat.toFixed(6)), Number(p.lng.toFixed(6))]);
      map.on(L.Draw.Event.CREATED, (e) => { const coords = coordsOf(e.layer); setArea(coords); persistArea(coords); });
      map.on(L.Draw.Event.EDITED, (e) => { let coords = null; e.layers.eachLayer((layer) => { coords = coordsOf(layer); }); if (coords) { setArea(coords); persistArea(coords); } });
    }

    const group = L.featureGroup(buildings.map((b) => L.marker([b.lat, b.lng])));
    map.fitBounds(group.getBounds().pad(0.4));
  }, [ready, buildings, canEdit, persistArea]); // eslint-disable-line

  // Shared layer overlay
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (sharedRef.current) { map.removeLayer(sharedRef.current); sharedRef.current = null; }
    if (shared && Array.isArray(shared.features) && shared.features.length) {
      sharedRef.current = L.geoJSON(shared, {
        pointToLayer: (f, ll) => L.marker(ll, { icon: spin((f.properties && f.properties.color) || "#0d9488") }),
        style: (f) => { const col = (f.properties && f.properties.color) || "#0d9488"; return f.geometry.type.indexOf("Polygon") >= 0 ? { color: col, weight: 2, fillColor: col, fillOpacity: 0.1, dashArray: "4 4" } : { color: col, weight: 3, opacity: 0.8, dashArray: "4 4" }; },
        onEachFeature: (f, layer) => { const t = f.properties && f.properties.label; if (t) layer.bindPopup(String(t)); },
      });
    }
  }, [shared, ready]);

  // Incidents overlay (rebuilds on data change)
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (incidentsGrpRef.current) { map.removeLayer(incidentsGrpRef.current); incidentsGrpRef.current = null; }
    const grp = new L.FeatureGroup();
    (incidents || []).forEach((inc) => {
      const pos = incLoc(inc); if (!pos) return;
      const P = PRIORITIES[inc.priority] || PRIORITIES[4];
      const cs = (inc.vehicles || []).map((v) => v.callsign).filter(Boolean).join(", ");
      const m = L.marker(pos, { icon: incIcon(P.hex) });
      m.bindPopup(`<b>${P.short} · ${inc.title}</b>${inc.building ? "<br/>" + inc.building.name : (inc.location_label ? "<br/>" + inc.location_label : "")}${cs ? "<br/>🚒 " + cs : ""}`);
      grp.addLayer(m);
    });
    incidentsGrpRef.current = grp;
  }, [incidents, ready]);

  // Draw / refresh the service-area polygon whenever `area` changes
  useEffect(() => {
    const map = mapRef.current, drawn = drawnRef.current;
    if (!map || !drawn) return;
    drawn.clearLayers();
    areaLayerRef.current = null;
    if (area && area.length >= 3) {
      const poly = L.polygon(area, { color: "#2563eb", weight: 2, fillColor: "#2563eb", fillOpacity: 0.1 });
      drawn.addLayer(poly);
      areaLayerRef.current = poly;
      map.fitBounds(poly.getBounds().pad(0.15));
    }
  }, [area]); // eslint-disable-line

  // Sync layer visibility to toggles
  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    const sync = (grp, on) => { if (!grp) return; if (on) { if (!map.hasLayer(grp)) grp.addTo(map); } else if (map.hasLayer(grp)) map.removeLayer(grp); };
    sync(buildingsGrpRef.current, layers.buildings);
    sync(waterGrpRef.current, layers.water);
    sync(incidentsGrpRef.current, layers.incidents);
    sync(sharedRef.current, layers.shared);
    sync(drawnRef.current, layers.area);
  }, [layers, ready, shared, incidents, water, buildings, area]);

  const clearArea = async () => {
    if (!(await confirmDialog({ title: "Verzorgingsgebied wissen", message: "Weet je zeker dat je het verzorgingsgebied wilt wissen?", danger: true, confirmText: "Wissen" }))) return;
    setArea(null);
    await persistArea([]);
  };

  const usedColors = new Set();
  if (shared && Array.isArray(shared.features)) shared.features.forEach((f) => usedColors.add((f.properties && f.properties.color) || "#0d9488"));
  const legendEntries = legend ? Object.entries(legend).filter(([c, v]) => v && usedColors.has(c)) : [];

  useRealtime((msg) => {
    if (!msg || !msg.type) return;
    if (msg.type === "map_layer_updated") api.get("/map-layer").then(({ data }) => setShared(data.enabled !== false ? data.drawings : null)).catch(() => {});
    else if (msg.type === "color_legend_updated") api.get("/settings/color-legend").then(({ data }) => setLegend(data.legend || null)).catch(() => {});
    else if (["incident_created", "incident_updated", "incident_closed", "incident_retracted", "incident_repaged"].includes(msg.type)) loadIncidents();
  });

  const toggle = (k) => setLayers((l) => ({ ...l, [k]: !l[k] }));
  const counts = { buildings: buildings.length, water: water.length, incidents: incidents.filter((i) => incLoc(i)).length, shared: (shared?.features || []).length, area: area && area.length >= 3 ? 1 : 0 };

  return (
    <div className="space-y-5" data-testid="map-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Kaart · Plattegrond</h1>
          <p className="text-sm text-slate-500">
            Verzorgingsgebied, gebouwen, bluswater en actieve incidenten
            {canEdit && <span className="ml-1 text-blue-600">· teken het verzorgingsgebied rechtsboven (gebouwen verplaats je in Beheer)</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canDrawShared && (
            <button data-testid="edit-shared-btn" onClick={() => setEditShared((v) => !v)}
              className={`inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-semibold shadow-sm ${editShared ? "border-teal-500 bg-teal-600 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
              <Layers className="h-4 w-4" /> Gedeelde laag {editShared ? "sluiten" : "bewerken"}
            </button>
          )}
          {canEdit && area && (
            <button data-testid="clear-area-btn" onClick={clearArea}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm hover:bg-red-50">
              <Trash2 className="h-4 w-4" /> Gebied wissen
            </button>
          )}
        </div>
      </div>
      <div className="grid gap-5 lg:grid-cols-4">
        <div className="lg:col-span-3">
          <MapShell heightClass="h-[600px]" testid="leaflet-map" onToggle={() => setTimeout(() => mapRef.current && mapRef.current.invalidateSize(), 80)}>
            <button data-testid="satellite-toggle" onClick={() => setSat((s) => !s)}
              className={`absolute left-3 top-3 z-[1001] rounded-lg border px-3 py-1.5 text-xs font-semibold shadow ${sat ? "border-blue-500 bg-blue-600 text-white" : "border-slate-200 bg-white/95 text-slate-700 hover:bg-white"}`}>
              {sat ? "Satelliet aan" : "Satelliet"}
            </button>
            <div ref={mapEl} data-testid="leaflet-map" className="h-full w-full overflow-hidden rounded-2xl border border-slate-200 shadow-sm" />
            {legendEntries.length > 0 && (
              <div data-testid="map-legend" className="absolute bottom-3 left-3 z-[1001] rounded-lg border border-slate-200 bg-white/95 px-3 py-2 shadow">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Legenda</div>
                <div className="space-y-1">
                  {legendEntries.map(([c, v]) => (
                    <div key={c} className="flex items-center gap-2">
                      <span className="h-3 w-3 shrink-0 rounded-full border border-white shadow" style={{ backgroundColor: c }} />
                      <span className="text-xs font-medium text-slate-700">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div data-testid="map-layer-legend" className="absolute bottom-3 right-3 z-[1001] rounded-lg border border-slate-200 bg-white/95 px-3 py-2 shadow">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Lagen</div>
              <div className="space-y-1">
                {LAYER_META.filter(({ key }) => layers[key]).map(({ key, label, color }) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="h-3 w-3 shrink-0 rounded-full border border-white shadow" style={{ backgroundColor: color }} />
                    <span className="text-xs font-medium text-slate-700">{label}</span>
                    <span className="ml-auto text-[10px] tabular-nums text-slate-400">{counts[key]}</span>
                  </div>
                ))}
              </div>
            </div>
          </MapShell>
        </div>
        <div className="space-y-3">
          <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="layer-panel">
            <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Layers className="h-3.5 w-3.5" /> Kaartlagen</div>
            <div className="space-y-1">
              {LAYER_META.map(({ key, label, icon: Icon, color }) => (
                <button key={key} data-testid={`layer-toggle-${key}`} onClick={() => toggle(key)}
                  className={`flex w-full items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-sm transition-colors ${layers[key] ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-60"}`}>
                  <Icon className="h-4 w-4 shrink-0" style={{ color }} />
                  <span className="flex-1 font-medium text-slate-800">{label}</span>
                  <span className="text-[11px] tabular-nums text-slate-400">{counts[key]}</span>
                  <span className={`h-4 w-7 shrink-0 rounded-full p-0.5 transition-colors ${layers[key] ? "bg-blue-600" : "bg-slate-300"}`}>
                    <span className={`block h-3 w-3 rounded-full bg-white transition-transform ${layers[key] ? "translate-x-3" : ""}`} />
                  </span>
                </button>
              ))}
            </div>
          </div>
          <div className="text-sm font-semibold text-slate-700">Gebouwen ({buildings.length})</div>
          {buildings.map((b) => (
            <div key={b.id} data-testid={`building-item-${b.id}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-xs font-bold text-white">{b.code}</div>
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold text-slate-900">{b.name}</div>
                <div className="truncate text-[11px] text-slate-400">{Number(b.lat).toFixed(4)}, {Number(b.lng).toFixed(4)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
      {editShared && <SharedLayerEditor canWrite={canDrawShared} onSaved={(gj) => setShared(gj)} />}
    </div>
  );
}
